import type { MouseEvent } from "react";
import { Link } from "react-router-dom";
import { Lock, Wand2 } from "lucide-react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import { cn } from "@/shared/lib/utils";

import type { TransplantNode } from "../../../api/types";
import { HOST_NODE_MAX, isFactRole, isImageBlockKey, parseToken } from "../../../model/hostNodes";
import { RewriteButton } from "../editor/RewriteButton";
import { displayToken, nodeSourceLabel, roleCn } from "./hostLabels";
import { NoImagePlaceholder } from "./NoImagePlaceholder";

export const FACT_LOCK_HINT = "事实字段只能改采集";

/**
 * 编辑页右列的节点编辑(host 模式,A.55 / A.56 / A.57):位置(页 · 块 · 字段)+ 角色 + 出处标签 + 文本框 + AI 重写 + 字数。
 * ★ 事实节点(fact-*)只读 + 锁;「AI 重写」照样画出来但禁用,title 说「事实字段只能改采集」,并给回采集页的链接 ——
 *   api 对这两口都是 403,前端不让用户撞上去。
 * ★ 字数上限 = 500 字 / 节点(A.56 的 PUT 校验);超了标红提示,保存会被 api 拒。
 * ★ AI 重写送的是框里此刻的值(A.36 `current`),按 token 发(A.55)。
 */
export function HostNodeEditor({
  token,
  node,
  collectPath,
  onCollectClick,
  onChange,
  onRewrite,
}: {
  token: string;
  node: TransplantNode;
  collectPath: string;
  /** 离开前的未保存闸(同 stub 编辑页的 guardLink)。 */
  onCollectClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  onChange: (value: string) => void;
  /** 回 true = 已把新值写进去(弹层关掉)。 */
  onRewrite: (instruction: string, current: string) => Promise<boolean>;
}) {
  const fact = isFactRole(node.role);
  const source = nodeSourceLabel(node.binding);
  const len = node.value.length;
  const over = len > HOST_NODE_MAX;
  const inputId = "host-node-value";
  const where = displayToken(token);

  return (
    <section
      aria-label="节点编辑"
      data-token={token}
      className={cn("space-y-2 border border-border bg-card p-4", CARD_WALL_RADIUS)}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label htmlFor={inputId} className="text-sm font-semibold text-foreground">
          {where}
        </Label>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="outline" className="normal-case tracking-normal">
            {roleCn(node.role)}
          </Badge>
          {source && (
            <Badge variant="secondary" className="normal-case tracking-normal">
              {source}
            </Badge>
          )}
          {fact ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled
              title={FACT_LOCK_HINT}
              aria-label={`AI 重写:${where}`}
              className="h-6 px-1.5 text-2xs"
            >
              <Wand2 className="h-3 w-3" />
              AI 重写
            </Button>
          ) : (
            <RewriteButton
              fieldLabel={where}
              onRewrite={(instruction) => onRewrite(instruction, node.value)}
            />
          )}
        </div>
      </div>

      {isImageBlockKey(parseToken(token)?.block ?? "") && (
        <NoImagePlaceholder className="mt-0 h-16" />
      )}

      <Textarea
        id={inputId}
        value={node.value}
        readOnly={fact}
        aria-invalid={over || undefined}
        rows={node.role === "para" ? 5 : 2}
        className={cn(fact && "bg-muted/40 text-muted-foreground")}
        onChange={(e) => onChange(e.target.value)}
      />

      <div className="flex flex-wrap items-center justify-between gap-2 text-2xs">
        {fact ? (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <Lock className="h-3 w-3" aria-hidden />
            {FACT_LOCK_HINT},
            <Link
              to={collectPath}
              onClick={onCollectClick}
              className="font-medium text-primary hover:underline"
            >
              去资料采集
            </Link>
          </span>
        ) : (
          <span className="text-muted-foreground">改动保存草稿后生效,发布后才会出现在站点上。</span>
        )}
        <span
          className={cn(
            "tabular-nums",
            over ? "font-semibold text-destructive" : "text-muted-foreground"
          )}
        >
          {len} / {HOST_NODE_MAX} 字{over && " · 超出上限"}
        </span>
      </div>
    </section>
  );
}
